import * as React from 'react';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';

import {
  Tooltip,
  StyledLink,
  StyledExpand,
  LetterAvatar,
  NavItemWrapper,
  StyledCollapse,
  StyledSubMenuLink,
} from './SideBarLinkElements';
import { Span, P } from '../../typography';

// helper methods
import { IS_VENDOR } from '../../consts/index';
import { theme } from '../../theme';
import { getArrayFromObject } from '../../utils/helpers';
import { SteynCityContext } from '../ContextProvider';

export type SideBarLinkProps = RouteComponentProps & {
  to: string;
  parent: string;
  bg?: string;
  open: boolean;
  title: string;
  color?: string;
  pathname?: string;
  hasSubMenu?: boolean;
  icon?: React.ReactNode;
  location?: { pathname?: string };
  item?: { to: string; title: string };
  map?: (item: object) => React.ReactNode;
  subMenu?: Array<{ to: string; title: string }>;
};

class SideBarLink extends React.Component<SideBarLinkProps, {}> {
  static defaultProps = {
    backgroundColor: 'transparent',
    color: '#FFF',
  };

  static contextType = SteynCityContext;
  context!: React.ContextType<typeof SteynCityContext>;

  state = {
    expanded: false,
  };

  componentDidMount() {
    const { location, parent } = this.props;
    if (location) {
      if (location.pathname.includes(parent)) {
        // we are in the this route so we open submenu.
        this.setState({ expanded: true });
      }
    }
  }

  isChildActive() {
    const { subMenu, location } = this.props;
    if (location) {
      return getArrayFromObject(subMenu).some(path => path.to === location.pathname);
    }
    return undefined;
  }

  isActive = () => {
    const { location, parent } = this.props;
    if (location) {
      return location.pathname.includes(parent);
    }
    return undefined;
  };

  toggleExpanded = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    const { subMenu } = this.props;
    if (!!subMenu) {
      this.setState({
        expanded: !this.state.expanded,
      });
    }
  };

  renderIcon = (icon: React.ReactNode) => {
    if (React.isValidElement(icon)) {
      return icon;
    }
    return null;
  };

  getLetters(title: string) {
    let matches = title.match(/\b(\w)/g);
    if (matches) {
      if (matches.length < 3) {
        return matches.join('');
      } else {
        matches = matches.slice(1, 3);
        return matches.join('');
      }
    }
    return '';
  }

  renderSubMenu = (subMenu: Array<{ to: string; title: string }>, { ...props }) => {
    return subMenu.map((item: { to: string; title: string }) => (
      <StyledSubMenuLink
        to={item.to}
        key={item.title}
        activeClassName="active-submenu-link"
        className={`${props.open ? 'nav-link' : 'nav-link-text-hidden'}`}
      >
        <NavItemWrapper hasSubMenu={true} open={props.open}>
          {props.open ? (
            <Span pointer={true} ml="60px" color={IS_VENDOR ? theme.colors.charcoalGrey : theme.colors.white}>
              {item.title}
            </Span>
          ) : (
            <LetterAvatar open={props.open}>
              <P className="abbreviated-letters">{this.getLetters(item.title)}</P>
              <Tooltip>
                <P p={0} m={0}>
                  {item.title}
                </P>
              </Tooltip>
            </LetterAvatar>
          )}
        </NavItemWrapper>
      </StyledSubMenuLink>
    ));
  };

  render() {
    const { icon, to, title, open, color, subMenu, bg } = this.props;
    const { expanded } = this.state;
    const hasSubMenu = !!subMenu;
    return (
      <React.Fragment>
        <StyledLink
          to={to}
          hasSubMenu={hasSubMenu}
          // Set timeout to ensure the animation of the sidebar closing is done AFTER the router has navigated
          onClick={() => setTimeout(() => this.context[this.context.drawerOpen ? 'closeDrawer' : 'openDrawer'](), 1)}
          activeClassName="active-nav-link"
          isActive={this.isActive}
          className={`${this.context.drawerOpen ? 'nav-link' : 'nav-link-text-hidden'}`}
        >
          <NavItemWrapper open={open} bg={bg}>
            {this.renderIcon(icon)}
            <Span pointer={true} ml="20px" color={color}>
              {title}
            </Span>
            {subMenu && <StyledExpand size={20} open={open} expanded={expanded} />}
            {!open && (
              <Tooltip>
                <P>{title}</P>
              </Tooltip>
            )}
          </NavItemWrapper>
        </StyledLink>
        <StyledCollapse active={this.isChildActive()} open={expanded} sidebarOpen={open}>
          {subMenu && typeof subMenu !== undefined && this.renderSubMenu(subMenu, { open, color })}
        </StyledCollapse>
      </React.Fragment>
    );
  }
}

export default withRouter(SideBarLink);



// WEBPACK FOOTER //
// ./src/components/sidebar/SideBarLink.tsx